import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CheckCircle2, XCircle, ShieldCheck, Loader2, AlertTriangle, Send } from 'lucide-react';
import { useAuth } from '@/app/providers/AuthProvider';
import { useFinance } from '@/contexts/FinanceContext';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const fmtBrl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export function PortalApprovalsPage() {
  const { user } = useAuth();
  const { client, clientId } = useCurrentClient();
  const { transactions, accounts, categories, approveTransactions, rejectTransactions, isLoading } =
    useFinance();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectIds, setRejectIds] = useState<string[]>([]);
  const [rejectionReason, setRejectionReason] = useState('');
  const [busy, setBusy] = useState(false);

  const awaiting = useMemo(
    () =>
      transactions
        .filter((t) => t.approvalStatus === 'awaiting' && t.status !== 'completed')
        .sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime()),
    [transactions],
  );

  const totalAmount = useMemo(() => awaiting.reduce((s, t) => s + Number(t.amount), 0), [awaiting]);
  const selectedAmount = useMemo(
    () => awaiting.filter((t) => selected.has(t.id)).reduce((s, t) => s + Number(t.amount), 0),
    [awaiting, selected],
  );

  const allSelected = awaiting.length > 0 && selected.size === awaiting.length;

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(awaiting.map((t) => t.id)));
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApprove = async (ids: string[]) => {
    if (!user?.id || ids.length === 0) return;
    setBusy(true);
    try {
      await approveTransactions(ids, user.id);
      setSelected(new Set());
    } finally {
      setBusy(false);
    }
  };

  const openReject = (ids: string[]) => {
    setRejectIds(ids);
    setRejectionReason('');
    setRejectOpen(true);
  };

  const confirmReject = async () => {
    if (rejectIds.length === 0) return;
    setBusy(true);
    try {
      await rejectTransactions(rejectIds, rejectionReason);
      setRejectOpen(false);
      setSelected(new Set());
    } finally {
      setBusy(false);
    }
  };

  if (!clientId) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-muted-foreground">
          <ShieldCheck className="mx-auto mb-3 h-10 w-10 opacity-40" />
          <p className="text-sm">Sem cliente selecionado.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-brand text-2xl font-bold text-foreground">Aprovações pendentes</h1>
        <p className="text-sm text-muted-foreground">
          {client?.name ? `${client.name} · ` : ''}pagamentos aguardando sua aprovação
        </p>
      </div>

      {Number(client?.auto_approval_limit ?? 0) > 0 && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs text-primary">
          <ShieldCheck size={14} className="mr-1.5 inline" />
          Limite de aprovação automática:{' '}
          <strong>{fmtBrl(Number(client?.auto_approval_limit ?? 0))}</strong>. Pagamentos abaixo
          desse valor não aparecem aqui — eles são executados sem precisar da sua aprovação.
        </div>
      )}

      {/* Resumo */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Pagamentos aguardando
            </p>
            <p className="mt-1 text-3xl font-bold tabular-nums text-foreground">
              {awaiting.length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Valor total
            </p>
            <p className="mt-1 text-3xl font-bold tabular-nums text-expense">
              {fmtBrl(totalAmount)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Barra de ações em lote */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3 backdrop-blur">
          <div className="text-sm">
            <strong>{selected.size}</strong> selecionado
            {selected.size === 1 ? '' : 's'} ·{' '}
            <span className="tabular-nums">{fmtBrl(selectedAmount)}</span>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="income"
              onClick={() => handleApprove(Array.from(selected))}
              disabled={busy}
            >
              <CheckCircle2 size={14} className="mr-1.5" />
              Aprovar todos
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-expense hover:bg-expense/10"
              onClick={() => openReject(Array.from(selected))}
              disabled={busy}
            >
              <XCircle size={14} className="mr-1.5" />
              Rejeitar todos
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelected(new Set())}
              disabled={busy}
            >
              Limpar
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : awaiting.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-income/40" />
            <p className="text-sm font-medium text-foreground">Tudo em dia.</p>
            <p className="mt-1 text-xs">Não há pagamentos aguardando sua aprovação no momento.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/40 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleAll}
                        aria-label="Selecionar todos"
                      />
                    </th>
                    <th className="px-3 py-2 text-left">Vencimento</th>
                    <th className="px-3 py-2 text-left">Descrição</th>
                    <th className="px-3 py-2 text-left">Categoria</th>
                    <th className="px-3 py-2 text-left">Conta</th>
                    <th className="px-3 py-2 text-right">Valor</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {awaiting.map((t) => {
                    const cat = categories.find((c) => c.id === t.categoryId);
                    const acc = accounts.find((a) => a.id === t.accountId);
                    const isSelected = selected.has(t.id);
                    const isOverdue = t.dueDate < new Date();
                    return (
                      <tr
                        key={t.id}
                        className={`border-b border-border/50 ${isSelected ? 'bg-primary/5' : ''}`}
                      >
                        <td className="px-3 py-2">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleOne(t.id)}
                            aria-label={`Selecionar ${t.description}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div className="inline-flex items-center gap-1">
                            {isOverdue && <AlertTriangle size={12} className="text-expense" />}
                            <span className={isOverdue ? 'font-medium text-expense' : ''}>
                              {format(t.dueDate, 'dd/MM/yyyy', { locale: ptBR })}
                            </span>
                          </div>
                        </td>
                        <td className="max-w-xs px-3 py-2">
                          <span className="truncate text-sm" title={t.description}>
                            {t.description}
                          </span>
                          {t.notes && (
                            <p className="truncate text-xs text-muted-foreground" title={t.notes}>
                              {t.notes}
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {cat ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                className="h-2 w-2 rounded-full"
                                style={{ backgroundColor: cat.color }}
                              />
                              {cat.name}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">{acc?.name ?? '—'}</td>
                        <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums text-expense">
                          {fmtBrl(Number(t.amount))}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="income"
                              onClick={() => handleApprove([t.id])}
                              disabled={busy}
                              className="h-7 px-2 text-xs"
                            >
                              <CheckCircle2 size={12} className="mr-1" />
                              Aprovar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openReject([t.id])}
                              disabled={busy}
                              className="h-7 px-2 text-xs text-expense hover:bg-expense/10"
                            >
                              <XCircle size={12} className="mr-1" />
                              Rejeitar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-brand text-lg">
              Rejeitar {rejectIds.length > 1 ? `${rejectIds.length} pagamentos` : 'pagamento'}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Sua equipe de consultoria será notificada. Conte o motivo (opcional, mas ajuda).
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="reject-reason" className="text-xs">
                Motivo
              </Label>
              <Textarea
                id="reject-reason"
                rows={3}
                placeholder="Ex: pagamento já realizado por outro meio…"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              onClick={confirmReject}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Rejeitando…
                </>
              ) : (
                <>
                  <Send size={14} className="mr-2" />
                  Confirmar rejeição
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
